import { useState, useEffect } from 'react';
// Rename the imported Form990Data to avoid conflict
import { Form990Data as ImportedForm990Data } from '../../../../types/form990.types';
import { Form990Service } from '../../../../services/Form990Service';

// First, let's define the correct interfaces for Contributions and Investment

interface Contributions {
  individuals: number;
  government: number;
  other: number;
  total?: number;
}

interface Investment {
  securities: number;
  rental: number;
  other: number;
  total?: number;
}

// Define our local Form990Data interface that extends the imported one
export interface Form990Data extends ImportedForm990Data {
  header: {
    ein: string;
    organizationName: string;
    dba?: string;
    taxYear: number;
    taxPeriodBeginDate: Date;
    taxPeriodEndDate: Date;
    filingType: string;
  };
  financials: {
    revenue: {
      programService: number;
      contributions: Contributions;
      investment: Investment;
      other: number;
      total: number;
    };
    expenses: {
      programServices: number;
      management: number;
      fundraising: number;
      total: number;
    };
    netAssets: {
      beginningOfYear: number;
      endOfYear: number;
    };
  };
  // ... other sections
}

// Extend the Form990Service to add the missing method
class ExtendedForm990Service extends Form990Service {
  async getForm990Data(ein: string, year: number): Promise<Form990Data> {
    // This would typically call an API or database
    // For now, return a mock implementation
    return {
      header: {
        ein,
        organizationName: 'Sample Organization',
        taxYear: year,
        taxPeriodBeginDate: new Date(year, 0, 1),
        taxPeriodEndDate: new Date(year, 11, 31),
        filingType: '990'
      },
      financials: {
        revenue: {
          programService: 0,
          contributions: {
            individuals: 0,
            government: 0,
            other: 0,
            total: 0
          },
          investment: {
            securities: 0,
            rental: 0,
            other: 0,
            total: 0
          },
          other: 0,
          total: 0
        },
        expenses: {
          programServices: 0,
          management: 0,
          fundraising: 0,
          total: 0
        },
        netAssets: {
          beginningOfYear: 0,
          endOfYear: 0
        }
      }
    } as Form990Data;
  }
}

export const useForm990Data = (ein: string, year: number) => {
  const [data, setData] = useState<Form990Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const service = new ExtendedForm990Service();
        const result = await service.getForm990Data(ein, year);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [ein, year]);

  const getEmptyForm990Data = (): Form990Data => {
    return {
      header: {
        ein: ein,
        organizationName: '',
        taxYear: year,
        taxPeriodBeginDate: new Date(year, 0, 1),
        taxPeriodEndDate: new Date(year, 11, 31),
        filingType: '990'
      },
      financials: {
        revenue: {
          programService: 0,
          contributions: {
            individuals: 0,
            government: 0,
            other: 0
          },
          investment: {
            securities: 0,
            rental: 0,
            other: 0
          },
          other: 0,
          total: 0
        },
        expenses: {
          programServices: 0,
          management: 0,
          fundraising: 0,
          total: 0
        },
        netAssets: {
          beginningOfYear: 0,
          endOfYear: 0
        }
      }
    } as Form990Data;
  };

  return {
    data: data || getEmptyForm990Data(),
    loading,
    error
  };
}; 